// ocr.ts
import Tesseract from 'tesseract.js';

export interface OCRWord {
  text: string;
  confidence: number;
  bbox?: { x0: number; y0: number; x1: number; y1: number };
  [k: string]: unknown;
}

export type OCRResult =
  | {
      success: true;
      text: string;
      confidence: number;
      words: OCRWord[];
    }
  | {
      success: false;
      text: '';
      confidence: 0;
      error: string;
    }

export interface OCRProgress {
  /** Human-readable status message from Tesseract */
  status: string;
  /** 0–100 UI-friendly percentage */
  progress: number;
}

export type OcrMode = 'fast' | 'standard' | 'long';

export interface OcrOptions {
  /** Tesseract language code (e.g., 'eng', 'fra', 'chi_sim') */
  language?: string;
  /** Processing mode affecting speed vs accuracy */
  mode?: OcrMode;
  /** Page segmentation mode, 0–13; see Tesseract PSM docs */
  psm?: number;
  /** Engine mode if exposed by your build (e.g., 0–3) */
  oem?: number;
  /** Optional whitelist to constrain recognition */
  whitelist?: string;
  /** Abort controller for cancellation */
  signal?: AbortSignal;
  /** Progress callback */
  onProgress?: (p: OCRProgress) => void;
}

// Mode configurations for different speed vs accuracy tradeoffs
function getModeConfig(mode: OcrMode) {
  switch (mode) {
    case 'fast':
      return {
        psm: 6, // Uniform block of text - faster
        oem: 0, // Legacy engine only - faster but less accurate
      };
    case 'long':
      return {
        psm: 13, // Raw line - more thorough analysis
        oem: 1, // Neural nets LSTM only - more accurate but slower
      };
    case 'standard':
    default:
      return {
        psm: 3, // Fully automatic page segmentation - balanced
        oem: 2, // Tesseract + LSTM - balanced speed/accuracy
      };
  }
}

export const SUPPORTED_LANGUAGES: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'eng', name: 'English' },
  { code: 'fra', name: 'French' },
  { code: 'deu', name: 'German' },
  { code: 'spa', name: 'Spanish' },
  { code: 'ita', name: 'Italian' },
  { code: 'por', name: 'Portuguese' },
  { code: 'rus', name: 'Russian' },
  { code: 'jpn', name: 'Japanese' },
  { code: 'kor', name: 'Korean' },
  { code: 'chi_sim', name: 'Chinese Simplified' },
] as const;

const SUPPORTED_CODES = new Set(SUPPORTED_LANGUAGES.map(l => l.code));

/**
 * Map Tesseract's logger statuses to a global progress band.
 * Bands are conservative; adjusts smoothly within each band via logger progress (0..1).
 */
const STAGE_BANDS: Record<string, { start: number; end: number }> = {
  'loading tesseract core': { start: 0, end: 10 },
  'initializing tesseract': { start: 10, end: 20 },
  'loading language traineddata': { start: 20, end: 40 },
  'initializing api': { start: 40, end: 50 },
  'recognizing text': { start: 50, end: 99 }, // leave 1% for finalization
};

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

function toUiProgress(status: string, stageProgress?: number): number {
  const band = STAGE_BANDS[status];
  if (!band) {
    // Unknown status: best-effort—don’t jump backwards if UI already higher.
    // Return a neutral mid value to avoid jitter.
    return 45;
  }
  const p = typeof stageProgress === 'number' ? clamp(stageProgress, 0, 1) : 0;
  return clamp(band.start + (band.end - band.start) * p, band.start, band.end);
}

function pickLanguage(requested?: string): string {
  if (requested && SUPPORTED_CODES.has(requested)) return requested;
  return 'eng';
}

/**
 * High-accuracy OCR with granular, realistic progress updates.
 */
export async function performOCR(
  imageFile: File,
  opts: OcrOptions = {}
): Promise<OCRResult> {
  const {
    language: requestedLang = 'eng',
    psm,
    oem,
    whitelist,
    signal,
    onProgress,
  } = opts;

  // Guard
  const v = validateImageFile(imageFile);
  if (!v.valid) {
    return { success: false, text: '', confidence: 0, error: v.error ?? 'Invalid image file' };
  }

  const lang = pickLanguage(requestedLang);

  // Configure OCR parameters based on mode
  const mode = opts.mode ?? 'standard';
  const modeConfig = getModeConfig(mode);

  let worker: any | null = null;
  let imageUrl: string | null = null;

  // Simple progress throttling to avoid UI spam
  let lastEmit = 0;
  const emit = (status: string, rawProgress?: number) => {
    const now = Date.now();
    if (now - lastEmit < 50) return; // ~20fps max
    lastEmit = now;
    const prog = Math.round(toUiProgress(status, rawProgress));
    onProgress?.({ status, progress: prog });
  };

  try {
    imageUrl = URL.createObjectURL(imageFile);

    // Create worker with language - Tesseract.js v6 API
    emit('loading tesseract core', 0);
    worker = await Tesseract.createWorker(lang);

    emit('initializing', 30);

    // Set parameters based on mode and user options
    const params: Record<string, string | number> = {};

    // Apply mode-based defaults, but allow user overrides
    params.tessedit_pageseg_mode = psm ?? modeConfig.psm;
    params.tessedit_ocr_engine_mode = oem ?? modeConfig.oem;

    // User-provided parameters take precedence
    if (typeof whitelist === 'string' && whitelist.length) {
      params.tessedit_char_whitelist = whitelist;
    }

    await worker.setParameters(params);
    emit('configuring parameters', 50);

    // Handle cancellation
    if (signal?.aborted) {
      throw new Error('OCR cancelled');
    }
    const abortHandler = () => {
      // Fast abort: terminate worker; caller can interpret as cancellation.
      try { worker?.terminate?.(); } catch { /* noop */ }
    };
    signal?.addEventListener('abort', abortHandler, { once: true });

    emit('recognizing text', 0);
    const result = await worker.recognize(imageUrl);

    signal?.removeEventListener('abort', abortHandler);

    // Finalize progress
    onProgress?.({ status: 'done', progress: 100 });

    return {
      success: true,
      text: result?.data?.text ?? '',
      confidence: typeof result?.data?.confidence === 'number' ? result.data.confidence : 0,
      words: Array.isArray(result?.data?.words) ? (result.data.words as OCRWord[]) : [],
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error occurred';
    // Provide a terminal progress update to help UIs settle.
    onProgress?.({ status: 'error', progress: 100 });
    return { success: false, text: '', confidence: 0, error: msg };
  } finally {
    if (imageUrl) {
      try { URL.revokeObjectURL(imageUrl); } catch { /* noop */ }
    }
    if (worker) {
      try { await worker.terminate(); } catch { /* noop */ }
    }
  }
}

export function validateImageFile(file: File): { valid: boolean; error?: string } {
  if (!file) return { valid: false, error: 'No file selected' };
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'Please select a valid image file' };
  }
  // 10 MB default cap
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    return { valid: false, error: 'File size must be less than 10MB' };
  }
  return { valid: true };
}
